import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

// Self-managed <video> — uses a ref so React reconciliation never
// recreates the element. Plays as soon as ready; loops; muted.
function VideoPlayer({ video, caption }) {
    const ref = useRef(null);

    useEffect(() => {
        const v = ref.current;
        if (!v) return;
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.loop = true;
        const tryPlay = () => {
            const p = v.play();
            if (p && typeof p.catch === "function") {
                p.catch(() => {
                    // autoplay blocked — at least show first frame
                    v.pause();
                });
            }
        };
        v.addEventListener("loadedmetadata", tryPlay);
        v.addEventListener("canplay", tryPlay);
        try {
            v.load();
        } catch (err) {
            console.warn("AssetPlaceholder: video load() failed", video, err);
        }
        tryPlay();
        return () => {
            v.removeEventListener("loadedmetadata", tryPlay);
            v.removeEventListener("canplay", tryPlay);
        };
    }, [video]);

    return (
        <video
            ref={ref}
            src={video}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label={caption}
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        />
    );
}

function MediaVideo({ video, label, fitClass }) {
    return (
        <video
            key={video}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label={label}
            className={`absolute inset-0 w-full h-full ${fitClass} select-none pointer-events-none`}
        >
            <source src={video} type="video/mp4" />
        </video>
    );
}

function MediaImage({ src, label }) {
    return (
        <img
            src={src}
            alt={label}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            draggable={false}
        />
    );
}

function PlaceholderPattern({ caption, children }) {
    return (
        <>
            {/* subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                }}
            />
            {/* corner accent */}
            <motion.div
                className="absolute top-3 left-3 w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--accent)", boxShadow: "0 0 12px var(--accent-soft)" }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute inset-0 flex items-center justify-center p-4">
                {children ?? (
                    <span
                        className="text-sm sm:text-base tracking-[0.25em] uppercase"
                        style={{ color: "var(--muted)" }}
                    >
                        {caption}
                    </span>
                )}
            </div>
        </>
    );
}

// Hairline placeholder panel used wherever a real asset will later drop in.
// Renders video > image > placeholder pattern, in that order of preference.
export function AssetPlaceholder({
    caption = "PLACEHOLDER",
    src = null,
    video = null,
    alt = "",
    className = "",
    children,
    rounded = "rounded-[20px]",
    aspect = "",
    fit = "cover",
}) {
    const hasMedia = !!(src || video);
    const fitClass = fit === "contain" ? "object-contain" : "object-cover";
    const label = alt || caption;

    let media;
    if (video) {
        media = <MediaVideo video={video} label={label} fitClass={fitClass} />;
    } else if (src) {
        media = <MediaImage src={src} label={label} />;
    } else {
        media = <PlaceholderPattern caption={caption}>{children}</PlaceholderPattern>;
    }

    return (
        <div
            className={`relative overflow-hidden border border-[var(--line)] ${rounded} ${aspect} ${className}`}
            style={{
                background: hasMedia
                    ? "#0C0C0C"
                    : "radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.04), rgba(255,255,255,0.01) 60%, transparent 100%), #0E0E0E",
            }}
        >
            {media}
        </div>
    );
}
